/**
 * Hooks de TanStack Query para Reporte Diario.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildRepos } from "@/core/data/repos";
import { porcionDeOt, porcionVacia } from "@features/recursos/porcionDeOt";
import { registrarAuditoria } from "@/core/audit/registrarAuditoria";
import { notificarTransicionRd, notificarCalificacionBajaRd } from "@core/mail/cableado";
import type { Rd } from "@app-types/entities";
import type { EstadoRd } from "@constants/estados";

export function useRds(tenantId: string) {
  return useQuery({
    queryKey: ["rds", tenantId],
    queryFn: () => buildRepos(tenantId).rd.list(),
    staleTime: 30_000,
  });
}

export function useRdsBySo(tenantId: string, soId: string | null) {
  return useQuery({
    queryKey: ["rds", tenantId, "bySo", soId],
    queryFn: () => (soId ? buildRepos(tenantId).rd.listBySo(soId) : Promise.resolve([])),
    enabled: !!soId,
  });
}

export function useRdsByEstado(tenantId: string, estado: EstadoRd | null) {
  return useQuery({
    queryKey: ["rds", tenantId, "byEstado", estado],
    queryFn: () => (estado ? buildRepos(tenantId).rd.listByEstado(estado) : Promise.resolve([])),
    enabled: !!estado,
  });
}

export function useRd(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["rd", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).rd.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useCreateRd(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Rd, "id" | "createdAt" | "updatedAt" | "version">) =>
      buildRepos(tenantId).rd.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rds", tenantId] });
    },
  });
}

/**
 * Crea/actualiza el RD del día de forma atómica (único punto de escritura del
 * "RD del día"). Lo usan todas las pestañas del RD embebido en la SO.
 */
export function useUpsertRdDia(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      soId,
      fecha,
      patch,
      createDefaults,
      userEmail,
    }: {
      soId: string;
      fecha: string;
      patch: Partial<
        Pick<Rd, "jornada" | "recursos" | "avancesTareas" | "eventos" | "fotos">
      >;
      createDefaults: Pick<Rd, "jornada" | "recursos" | "avancesTareas" | "eventos">;
      userEmail: string;
    }) => buildRepos(tenantId).rd.upsertDia(soId, fecha, patch, createDefaults, userEmail),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rds", tenantId] });
    },
  });
}

export function useUpdateRd(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, version }: { id: string; data: Partial<Rd>; version: number }) =>
      buildRepos(tenantId).rd.update(id, data, version),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["rd", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["rds", tenantId] });
    },
  });
}

function invalidateAfterRdMutation(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string,
  rdId?: string,
) {
  if (rdId) void qc.invalidateQueries({ queryKey: ["rd", tenantId, rdId] });
  void qc.invalidateQueries({ queryKey: ["rds", tenantId] });
}

export function useChangeRdState(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nextState,
      userEmail,
      userName,
      observaciones,
    }: {
      id: string;
      nextState: EstadoRd;
      userEmail: string;
      userName?: string;
      observaciones?: string;
    }) => {
      const repos = buildRepos(tenantId);
      const repo = repos.rd;
      const antes = await repo.get(id);
      const estadoAnterior = antes?.estado;
      const nivelAnterior = antes?.nivelRevision;

      // ── CONGELADO DE RECURSOS AL APROBAR ──────────────────────────────
      //
      // Los recursos los declara el contratista en su Registro de recursos, y
      // el reporte los lee en vivo mientras es borrador. Al aprobarse se COPIAN
      // dentro del reporte, antes de cambiar el estado: `changeState` calcula
      // la firma sobre `rd.recursos`, así que congelar después dejaría la firma
      // certificando unos recursos y el documento mostrando otros.
      //
      // Congelar —y no leer siempre en vivo— es lo que permite que el PDF
      // firmado siga valiendo cuando el contratista corrija días posteriores.
      if (nextState === "APROBADO" && antes) {
        const so = await repos.so.get(antes.soId);
        // Por RAZÓN SOCIAL: es la llave con la que el módulo guarda el
        // registro. `So.contratistaId` no sirve — trae "SOFORESTA" donde el
        // usuario trae "soforesta", así que nunca encontraría el registro.
        if (so?.contratistaNombre) {
          const registro = await repos.registroRecursos.getDelDia(
            so.contratistaNombre,
            antes.fecha,
          );
          const porcion = porcionDeOt(registro, antes.soId);
          // Sin registro del día no se pisa lo que el reporte ya traía: los
          // reportes anteriores al módulo tienen sus recursos embebidos y
          // deben conservarlos.
          if (!porcionVacia(porcion)) {
            await repo.update(id, { recursos: porcion }, antes.version);
          }
        }
      }

      await repo.changeState(id, nextState, userEmail, observaciones);
      const despues = await repo.get(id);
      if (despues && estadoAnterior) {
        void registrarAuditoria(tenantId, {
          usuario: userEmail,
          accion: `RD_${nextState}`,
          entidad: "RD",
          entidadId: id,
          diff: {
            estadoAnterior,
            estadoNuevo: nextState,
            ...(nivelAnterior !== undefined && { nivelAnterior }),
            ...(despues.nivelRevision !== undefined && { nivelNuevo: despues.nivelRevision }),
            ...(observaciones ? { observaciones } : {}),
          },
        });
        void notificarTransicionRd({
          tenantId,
          rd: despues,
          estadoAnterior,
          estadoNuevo: nextState,
          ...(nivelAnterior !== undefined && { nivelAnterior }),
          ...(despues.nivelRevision !== undefined && { nivelNuevo: despues.nivelRevision }),
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          ...(observaciones !== undefined && { motivo: observaciones }),
        });
        // RD-11: si al pasar a APROBADO la calificación INT es ≤ 2 estrellas.
        if (nextState === "APROBADO") {
          void notificarCalificacionBajaRd({
            tenantId,
            rd: despues,
            actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          });
        }
      }
    },
    onSuccess: (_, { id }) => invalidateAfterRdMutation(qc, tenantId, id),
  });
}

/**
 * Cambios de sub-flags del RD: nivelRevision (INT → PL), calificación INT,
 * observaciones del INT — sin cambio de estado oficial.
 *
 * Dispara RD-3 / RD-6 cuando el sub-flag `nivelRevision` cambia entre INT y PL
 * sin transición de estado oficial.
 */
export function useUpdateRdFlags(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      flags,
      userEmail,
      userName,
    }: {
      id: string;
      flags: {
        nivelRevision?: "INT" | "PL" | null;
        calificacionInt?: number;
        observacionesInt?: string;
      };
      userEmail: string;
      userName?: string;
    }) => {
      const repo = buildRepos(tenantId).rd;
      const antes = await repo.get(id);
      await repo.updateSubFlags(id, flags, userEmail);
      const despues = await repo.get(id);
      void registrarAuditoria(tenantId, {
        usuario: userEmail,
        accion: "RD_NIVEL_REVISION",
        entidad: "RD",
        entidadId: id,
        diff: {
          ...flags,
          ...(antes?.nivelRevision !== undefined && { nivelAnterior: antes.nivelRevision }),
          ...(despues?.nivelRevision !== undefined && { nivelNuevo: despues.nivelRevision }),
        },
      });
      if (despues && antes && despues.estado === "EN_REVISION") {
        const cambioNivel = antes.nivelRevision !== despues.nivelRevision;
        if (cambioNivel) {
          void notificarTransicionRd({
            tenantId,
            rd: despues,
            estadoAnterior: "EN_REVISION",
            estadoNuevo: "EN_REVISION",
            ...(antes.nivelRevision !== undefined && { nivelAnterior: antes.nivelRevision }),
            ...(despues.nivelRevision !== undefined && { nivelNuevo: despues.nivelRevision }),
            actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          });
        }
      }
      // RD-11: si el INT bajó la calificación a ≤2 sin transición de estado
      // (vía sub-flag, no vía APROBADO), igualmente disparamos el handover.
      if (
        despues &&
        antes &&
        antes.calificacionInt !== despues.calificacionInt &&
        despues.calificacionInt !== undefined &&
        despues.calificacionInt <= 2
      ) {
        void notificarCalificacionBajaRd({
          tenantId,
          rd: despues,
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
        });
      }
    },
    onSuccess: (_, { id }) => invalidateAfterRdMutation(qc, tenantId, id),
  });
}

export function useDeleteRd(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buildRepos(tenantId).rd.delete(id),
    onSuccess: (_, id) => invalidateAfterRdMutation(qc, tenantId, id),
  });
}

/**
 * Abre o cierra la ventana de ajuste de RECURSOS sobre un reporte aprobado.
 *
 * # Por qué una ventana y no reabrir el reporte
 *
 * Reabrirlo (`APROBADO → BORRADOR`) tiene tres efectos que aquí no se quieren:
 * el reporte deja de ganar valor y el EV del AFE cae; la regla FIFO bloquea la
 * aprobación de los reportes posteriores; y quedan expuestos los avances
 * cuando lo único mal digitado son los recursos.
 *
 * La ventana descongela SOLO los recursos. El reporte sigue aprobado y
 * contando.
 *
 * # Al cerrar, la firma cambia — y debe cambiar
 *
 * `firmaSha256` cubre `recursos`. Si el contenido certificado se corrigió, la
 * firma que lo avala tiene que recalcularse; dejarla intacta certificaría unos
 * recursos mientras el documento muestra otros.
 */
export function useAjusteRecursosRd(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      accion: "abrir" | "cerrar";
      motivo?: string;
      userEmail: string;
    }) => {
      const repos = buildRepos(tenantId);
      const rd = await repos.rd.get(args.id);
      if (!rd) throw new Error("Reporte no encontrado");
      if (rd.estado !== "APROBADO") {
        throw new Error(
          "La ventana de ajuste solo aplica a reportes APROBADOS. " +
            "Si el reporte aún no se aprueba, los recursos ya se leen en vivo.",
        );
      }
      const now = new Date().toISOString();

      if (args.accion === "abrir") {
        const motivo = (args.motivo ?? "").trim();
        // Sin motivo no se autoriza: la traza de POR QUÉ se corrigió es
        // justamente lo que hoy falta para explicar una cifra rara.
        if (!motivo) throw new Error("Indica el motivo del ajuste.");
        await repos.rd.update(
          args.id,
          {
            ajusteRecursos: { autorizadoPor: args.userEmail, autorizadoEn: now, motivo },
            historial: [
              ...(rd.historial ?? []),
              {
                fecha: now,
                estadoAnterior: rd.estado,
                estadoNuevo: rd.estado,
                usuario: args.userEmail,
                observaciones: `Ajuste de recursos autorizado: ${motivo}`,
              },
            ],
          },
          rd.version,
        );
        return;
      }

      // Cerrar: re-congelar la porción vigente del registro y volver a firmar.
      const so = await repos.so.get(rd.soId);
      const registro = so?.contratistaNombre
        ? await repos.registroRecursos.getDelDia(so.contratistaNombre, rd.fecha)
        : null;
      const porcion = porcionDeOt(registro, rd.soId);

      await repos.rd.update(
        args.id,
        {
          // Si el registro quedó vacío se conserva lo que había: cerrar la
          // ventana no puede borrar los recursos del reporte.
          ...(porcionVacia(porcion) ? {} : { recursos: porcion }),
          ajusteRecursos: {
            ...(rd.ajusteRecursos ?? { autorizadoPor: "", autorizadoEn: now, motivo: "" }),
            cerradoEn: now,
            cerradoPor: args.userEmail,
          },
          historial: [
            ...(rd.historial ?? []),
            {
              fecha: now,
              estadoAnterior: rd.estado,
              estadoNuevo: rd.estado,
              usuario: args.userEmail,
              observaciones: "Ajuste de recursos cerrado — recursos re-congelados",
            },
          ],
        },
        rd.version,
      );

      // La firma se recalcula sobre el reporte YA actualizado.
      const actualizado = await repos.rd.get(args.id);
      if (actualizado) {
        const firma = await repos.rd.calculateSignature(args.id);
        await repos.rd.update(args.id, { firmaSha256: firma }, actualizado.version);
      }
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["rd", tenantId, id] });
      void qc.invalidateQueries({ queryKey: ["rds"] });
    },
  });
}
